import { createClient } from '@supabase/supabase-js';

// 🔒 CRITICAL: These MUST be from the SAME Supabase project
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

// 🚨 ENHANCED: Detect credential mismatch early
const validateCredentials = (): boolean => {
  if (!supabaseUrl || supabaseUrl === 'undefined' || supabaseUrl === 'your-supabase-url') {
    console.error('❌ SUPABASE_URL is missing or invalid:', supabaseUrl);
    console.error('⚠️ Supabase client will NOT be initialized. Database operations will fail gracefully.');
    return false;
  }

  if (!supabaseAnonKey || supabaseAnonKey === 'undefined' || supabaseAnonKey === 'your-supabase-anon-key') {
    console.error('❌ SUPABASE_ANON_KEY is missing or invalid');
    console.error('⚠️ Supabase client will NOT be initialized. Database operations will fail gracefully.');
    return false;
  }

  // Validate URL format
  try {
    const urlObj = new URL(supabaseUrl);
    
    // 🆕 Extract project ID from URL for diagnostics
    const projectId = urlObj.hostname.split('.')[0];
    console.log('📍 Supabase Project ID from URL:', projectId);
    
    // 🆕 Check if anon key format matches expected pattern
    if (!supabaseAnonKey.startsWith('eyJ')) {
      console.error('❌ Invalid ANON_KEY format - should start with "eyJ"');
      console.error('⚠️ This is a JWT token - make sure you copied the complete key');
      return false;
    }
    
    console.log('✅ Credential format validation passed');
  } catch (e) {
    console.error('❌ Invalid Supabase URL format:', supabaseUrl);
    console.error('⚠️ Expected format: https://your-project.supabase.co');
    return false;
  }

  return true;
};

// 🎯 SAFE CLIENT CREATION: Never throws, returns null if invalid
let supabaseClient: any = null;

try {
  if (validateCredentials()) {
    try {
      supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
        },
        global: {
          headers: {
            'x-application-name': 'veriqon-landing',
          },
        },
        db: {
          schema: 'public',
        },
        realtime: {
          params: {
            eventsPerSecond: 10,
          },
        },
      });
      
      console.log('✅ Supabase client created successfully:', {
        url: supabaseUrl,
        keyPrefix: supabaseAnonKey?.substring(0, 20) + '...'
      });
    } catch (createError: any) {
      console.error('❌ Failed to create Supabase client:', createError.message);
      console.error('⚠️ This is usually caused by invalid credentials or network issues');
      supabaseClient = null;
    }
  } else {
    console.error('❌ Supabase client creation skipped due to invalid credentials');
  }
} catch (outerError: any) {
  console.error('❌ Critical error during Supabase client setup:', outerError.message);
  supabaseClient = null;
}

// Export the client (may be null if credentials are invalid)
export const supabase = supabaseClient;

// 🔍 Comprehensive connection test with credential mismatch detection
export const testSupabaseConnection = async (): Promise<{
  success: boolean;
  error?: string;
  details?: Record<string, any>;
}> => {
  console.log('🔍 Testing Supabase connection...');
  
  if (!supabase) {
    return {
      success: false,
      error: 'Supabase client not initialized',
      details: {
        reason: 'Invalid or missing credentials',
        supabaseUrl,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  try {
    // Test 1: Check if Supabase REST API is reachable
    const healthCheck = await fetch(`${supabaseUrl}/rest/v1/`, {
      method: 'HEAD',
      headers: {
        'apikey': supabaseAnonKey,
      },
    });
    
    if (!healthCheck.ok) {
      throw new Error(`Supabase health check failed: ${healthCheck.status} ${healthCheck.statusText}`);
    }
    
    console.log('✅ Supabase REST API is reachable');
    
    // Test 2: Try to query the database (this will tell us if credentials are valid)
    const { data, error } = await supabase
      .from('pilot_applications')
      .select('count', { count: 'exact', head: true });
    
    if (error) {
      console.error('❌ Database query failed:', error);
      throw new Error(`Database query failed: ${error.message}`);
    }
    
    console.log('✅ Database connection successful');
    
    return {
      success: true,
      details: {
        supabaseUrl,
        restApiStatus: 'healthy',
        databaseStatus: 'connected',
        timestamp: new Date().toISOString()
      }
    };
  } catch (err: any) {
    console.error('❌ Supabase connection test failed:', err);
    
    // 🆕 Enhanced error diagnostics with credential mismatch detection
    const errorDetails: Record<string, any> = {
      message: err.message,
      timestamp: new Date().toISOString(),
      supabaseUrl,
    };
    
    // 🔥 NEW: Detect credential mismatch
    if (err.message.includes('fetch failed') || err.message.includes('Failed to fetch')) {
      errorDetails.likelyCause = '🚨 CREDENTIAL MISMATCH - URL and API key are from different Supabase projects';
      errorDetails.explanation = 'The ANON_KEY does not match the project URL. This causes "fetch failed" errors.';
      errorDetails.criticalFix = [
        '1. Go to your Supabase dashboard: https://supabase.com/dashboard',
        '2. Open the CORRECT project that matches your URL',
        '3. Navigate to: Settings → API',
        '4. Copy BOTH values from the SAME project:',
        '   - Project URL (e.g., https://your-project.supabase.co)',
        '   - anon public key (the long JWT token starting with "eyJ")',
        '5. Update your .env file with BOTH values',
        '6. Restart your dev server completely'
      ];
    } else if (err.message.includes('401') || err.message.includes('Invalid API key')) {
      errorDetails.likelyCause = 'Invalid credentials';
      errorDetails.suggestions = [
        'Verify NEXT_PUBLIC_SUPABASE_ANON_KEY in .env file',
        'Make sure you are using the anon/public key, not the service key'
      ];
    } else if (err.message.includes('404')) {
      errorDetails.likelyCause = 'Project not found or table does not exist';
      errorDetails.suggestions = [
        'Verify NEXT_PUBLIC_SUPABASE_URL in .env file',
        'Check if pilot_applications table exists in your Supabase dashboard'
      ];
    }
    
    return {
      success: false,
      error: err.message,
      details: errorDetails
    };
  }
};

// 🔧 Enhanced diagnostics with credential matching verification
export const diagnoseSupabaseConnection = async () => {
  console.log('🔍 Running Supabase connection diagnostics...\n');
  
  // Check 1: Environment variables
  console.log('📋 Step 1: Checking environment variables...');
  console.log(`   NEXT_PUBLIC_SUPABASE_URL: ${supabaseUrl ? '✅ Set' : '❌ Missing'}`);
  console.log(`   NEXT_PUBLIC_SUPABASE_ANON_KEY: ${supabaseAnonKey ? '✅ Set' : '❌ Missing'}`);
  
  // Check 2: URL format and project ID extraction
  console.log('\n📋 Step 2: Validating URL format and extracting project ID...');
  try {
    const url = new URL(supabaseUrl);
    const projectId = url.hostname.split('.')[0];
    console.log(`   URL format: ✅ Valid`);
    console.log(`   Project ID from URL: ${projectId}`);
    console.log(`   🚨 IMPORTANT: Your ANON_KEY MUST be from project "${projectId}"`);
  } catch (e) {
    console.log(`   URL format: ❌ Invalid`);
    return;
  }
  
  // Check 3: Network connectivity
  console.log('\n📋 Step 3: Testing network connectivity...');
  const result = await testSupabaseConnection();
  
  if (result.success) {
    console.log('   Connection: ✅ Success');
    console.log('\n✅ All checks passed! Supabase is connected.');
  } else {
    console.log('   Connection: ❌ Failed');
    console.log(`\n❌ Error: ${result.error}`);
    if (result.details) {
      console.log('\n📊 Diagnostic Details:');
      console.log(JSON.stringify(result.details, null, 2));
    }
  }
  
  return result;
};